import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useDeliveryManagement } from "./useHook";
import ModalAddCities from "../../Components/Modals/Cities/City";
import { MdDelete } from "react-icons/md";
import Loader from "../../Utils/loader";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import { FiEdit2, FiPlus } from "react-icons/fi";

function Cities() {
  const { addCities, getCities, deleteCity, updateCity, getOutStationZones } =
    useDeliveryManagement();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [editCity, setEditCity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setEditCity(null);
  };

  const fetchCities = async () => {
    try {
      setIsLoading(true);
      const response = await getCities(setCities, offset, limit);

      if (response?.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
    getOutStationZones(setZones);
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleAddOrUpdateCity = async (
    cityName,
    cityId = null,
    zoneId,
    inStation,
  ) => {
    try {
      const cityData = {
        name: cityName,
        zone_id: zoneId,
        is_instation: inStation,
      };

      if (cityId) {
        const response = await updateCity(cityId, cityData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "City Updated",
            text: "Your city has been successfully updated!",
            confirmButtonText: "OK",
          });
        }
      } else {
        const response = await addCities(cityData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "City Added",
            text: "Your city has been successfully added!",
            confirmButtonText: "OK",
          });
        }
      }
      await fetchCities();
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Already Added",
        text: "Only one city can be set as instation.",
        confirmButtonText: "OK",
      });
    }
    closeModal();
  };

  const handleEditCity = (city) => {
    setEditCity(city);
    openModal();
  };

  const handleDeleteCity = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteCity(id);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "The city has been deleted.",
            confirmButtonText: "OK",
          });
          await fetchCities();
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to Delete",
          text: "An error occurred while deleting the city. Please try again.",
          confirmButtonText: "OK",
        });
      }
    }
  };

  if (isLoading) {
    return <Loader message="Loading cities..." />;
  }

  return (
    <div>
      <div>
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <h2 className="text-2xl font-bold">Manage Cities</h2>
          <button onClick={openModal} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add City
          </button>
        </div>

        <div className="bg-white w-full rounded-lg pagination-shadow my-5">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th">Sr. No</th>
                  <th className="table-th">City Name</th>
                  <th className="table-th">Zone Name</th>
                  <th className="table-th">In Station</th>
                  <th className="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                {cities.length > 0 ? (
                  cities.map((city, index) => (
                    <tr
                      key={city.id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                    >
                      <td className="table-td">{offset + index + 1}</td>
                      <td className="table-td">{city?.name}</td>
                      <td className="table-td">
                        {city?.zone?.zone_name || "Lahore"}
                      </td>
                      <td className="table-td">
                        {city?.is_instation ? "True" : "False"}
                      </td>
                      <td className="table-td flex space-x-2 mt-2">
                        <button
                          className="btn btn-edit text-white"
                          onClick={() => handleEditCity(city)}
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                        <button
                          className="btn btn-delete text-white"
                          onClick={() => handleDeleteCity(city.id)}
                        >
                          <MdDelete className="text-white" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-6 text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Component */}
          {pagination.totalPages > 1 && (
            <div className="mt-6">
              <GlobalPagination
                offset={offset}
                limit={limit}
                total={pagination.totalRecords}
                onPageChange={handlePageChange}
                className="mt-4"
              />
            </div>
          )}
        </div>
      </div>

      <ModalAddCities
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleAddOrUpdateCity}
        editCity={editCity}
        zones={zones}
      />
    </div>
  );
}

export default Cities;
